import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { gql } from '~/__generated__'
import { Button } from '~/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/ui/card'
import { Badge } from '~/ui/badge'
import { Plus, MessageSquare, Clock, Volume2, VolumeX, RotateCcw, Edit, Trash2 } from 'lucide-react'
import {
  newVoiceMessagePath,
  editVoiceMessagePath,
  voiceMessageSchedulesPath,
} from '~/common/paths'
import { useSafeMutation } from '~/common/use-safe-mutation'
import { formatDistanceToNow } from 'date-fns'
import { VoiceMessagingSummary } from './components/voice-messaging-summary'
import { AudioPlayer } from './components/audio-player'
import { useCurrentOrganization } from '~/auth/use-current-organization'

const VOICE_MESSAGES_QUERY = gql(`
  query VoiceMessages($organizationId: ID!) {
    voiceMessages(organizationId: $organizationId) {
      id
      text
      title
      ttsStatus
      ttsErrorMessage
      audioUrl
      durationSeconds
      enabled
      createdAt
      updatedAt
      campaigns {
        id
        name
        nextOccurrence
        enabled
      }
      durationFormatted
      displayName
      readyForPlayback
      hasActiveSchedules
    }
  }
`)

const DELETE_VOICE_MESSAGE_MUTATION = gql(`
  mutation DeleteVoiceMessage($input: DeleteVoiceMessageInput!) {
    deleteVoiceMessage(input: $input) {
      success
      errors
    }
  }
`)

const REGENERATE_TTS_MUTATION = gql(`
  mutation RegenerateTts($input: RegenerateVoiceMessageTtsInput!) {
    regenerateVoiceMessageTts(input: $input) {
      voiceMessage {
        id
        ttsStatus
      }
      errors
    }
  }
`)

export const VoiceMessagesScreen = () => {
  const navigate = useNavigate()
  const { id: organizationId } = useCurrentOrganization()

  const { data, loading, error, refetch } = useQuery(VOICE_MESSAGES_QUERY, {
    variables: { organizationId: organizationId ?? '' },
    skip: !organizationId,
    pollInterval: 5000, // Poll every 5 seconds to update TTS status
  })

  const [deleteVoiceMessage] = useSafeMutation(DELETE_VOICE_MESSAGE_MUTATION)
  const [regenerateTts] = useSafeMutation(REGENERATE_TTS_MUTATION)

  const handleDelete = async (id: string) => {
    if (confirm('Are you sure you want to delete this voice message?')) {
      const result = await deleteVoiceMessage({
        variables: { input: { id } },
        refetchQueries: [{ query: VOICE_MESSAGES_QUERY as any, variables: { organizationId } }],
      })

      if (result.data?.deleteVoiceMessage?.success) {
        // Success handled by refetch
      }
    }
  }

  const handleRegenerateTts = async (id: string) => {
    await regenerateTts({
      variables: { input: { id } },
      refetchQueries: [{ query: VOICE_MESSAGES_QUERY as any, variables: { organizationId } }],
    })
  }

  const getTtsStatusBadge = (status: string, errorMessage?: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary">Pending</Badge>
      case 'processing':
        return <Badge variant="secondary">Processing...</Badge>
      case 'completed':
        return <Badge variant="default">Ready</Badge>
      case 'failed':
        return (
          <Badge variant="destructive" title={errorMessage}>
            Failed
          </Badge>
        )
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  if (loading) {
    return (
      <div className="flex h-screen flex-col">
        <div className="flex-shrink-0 border-b p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Voice Messages</h1>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="animate-pulse">
                <CardHeader>
                  <div className="h-4 w-3/4 rounded bg-gray-200"></div>
                  <div className="h-3 w-1/2 rounded bg-gray-200"></div>
                </CardHeader>
                <CardContent>
                  <div className="h-3 w-full rounded bg-gray-200"></div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-screen flex-col">
        <div className="flex-shrink-0 border-b p-6">
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold">Voice Messages</h1>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="py-12 text-center">
            <p className="text-red-600">Error loading voice messages: {error.message}</p>
            <Button onClick={() => refetch()} className="mt-4">
              Try Again
            </Button>
          </div>
        </div>
      </div>
    )
  }

  const voiceMessages = data?.voiceMessages || []

  return (
    <div className="flex h-full flex-col">
      <div className="sticky top-0 flex-shrink-0 border-b bg-white p-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">Voice Messages</h1>
            <p className="text-muted-foreground">
              Manage text-to-speech announcements for your zones
            </p>
          </div>
          <Button onClick={() => navigate(newVoiceMessagePath({}))}>
            <Plus className="mr-2 h-4 w-4" />
            New Voice Message
          </Button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-6 pb-20">
        {/* Voice Messaging Summary */}
        {voiceMessages.length > 0 && (
          <div className="mb-6">
            <VoiceMessagingSummary
              voiceMessages={voiceMessages.map((vm) => ({
                id: vm.id,
                ttsStatus: vm.ttsStatus,
                enabled: vm.enabled,
                readyForPlayback: vm.readyForPlayback,
                hasActiveSchedules: vm.hasActiveSchedules,
                campaigns: (vm.campaigns || []).map((c: any) => ({
                  enabled: c.enabled,
                  nextOccurrence: c.nextOccurrence || undefined,
                })),
              }))}
              zoneName={undefined}
            />
          </div>
        )}

        {voiceMessages.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <MessageSquare className="text-muted-foreground mx-auto mb-4 h-12 w-12" />
              <h3 className="mb-2 text-lg font-semibold">No voice messages yet</h3>
              <p className="text-muted-foreground mb-6">
                Create your first voice message to get started with automated announcements.
              </p>
              <Button onClick={() => navigate(newVoiceMessagePath({}))}>
                <Plus className="mr-2 h-4 w-4" />
                Create Voice Message
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {voiceMessages.map((voiceMessage: any) => (
              <Card key={voiceMessage.id} className="transition-shadow hover:shadow-md">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="mb-2 text-lg">{voiceMessage.displayName}</CardTitle>
                      <CardDescription className="text-sm">"{voiceMessage.text}"</CardDescription>
                    </div>
                    <div className="ml-4 flex items-center gap-2">
                      {getTtsStatusBadge(voiceMessage.ttsStatus, voiceMessage.ttsErrorMessage)}
                      {voiceMessage.enabled ? (
                        <Volume2 className="h-4 w-4 text-green-600" />
                      ) : (
                        <VolumeX className="h-4 w-4 text-gray-400" />
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between">
                    <div className="text-muted-foreground flex items-center gap-6 text-sm">
                      {voiceMessage.durationFormatted && (
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          {voiceMessage.durationFormatted}
                        </div>
                      )}
                      <div>Created {formatDistanceToNow(new Date(voiceMessage.createdAt))} ago</div>
                      {(voiceMessage.campaigns?.length ?? 0) > 0 && (
                        <span className="text-muted-foreground">
                          {voiceMessage.campaigns?.length} campaign{(voiceMessage.campaigns?.length ?? 0) !== 1 ? 's' : ''}
                        </span>
                      )}
                    </div>

                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(voiceMessageSchedulesPath({ voiceMessageId: voiceMessage.id }))
                        }
                      >
                        <Clock className="mr-1 h-4 w-4" />
                        Campaigns
                      </Button>
                      {voiceMessage.ttsStatus === 'failed' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleRegenerateTts(voiceMessage.id)}
                        >
                          <RotateCcw className="mr-1 h-4 w-4" />
                          Retry TTS
                        </Button>
                      )}

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          navigate(editVoiceMessagePath({ voiceMessageId: voiceMessage.id }))
                        }
                      >
                        <Edit className="mr-1 h-4 w-4" />
                        Edit
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(voiceMessage.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="mr-1 h-4 w-4" />
                        Delete
                      </Button>
                    </div>
                  </div>

                  {/* Show audio player for completed messages */}
                  {voiceMessage.audioUrl && voiceMessage.ttsStatus === 'completed' && (
                    <div className="mt-4 border-t pt-4">
                      <div className="mb-2 text-sm font-medium">Audio Preview</div>
                      <AudioPlayer
                        audioUrl={voiceMessage.audioUrl}
                        title={voiceMessage.displayName}
                      />
                    </div>
                  )}

                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
